import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppealRequestStatus } from "types";

// Enhanced status chip with better styling
export function getStatusChipColor(status: string) {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  switch (status) {
    case AppealRequestStatus.PENDING:
      return `${baseClasses} bg-amber-50 text-amber-800 border-amber-200`;
    case AppealRequestStatus.FORWARDED_TO_REGISTRAR:
      return `${baseClasses} bg-blue-50 text-blue-800 border-blue-200`;
    case AppealRequestStatus.FORWARDED_TO_COORDINATOR:
      return `${baseClasses} bg-purple-50 text-purple-800 border-purple-200`;
    case AppealRequestStatus.SUBMITTED_TO_COURT:
      return `${baseClasses} bg-emerald-50 text-emerald-800 border-emerald-200`;
    case AppealRequestStatus.COORDINATOR_RESPONDED_BACK:
      return `${baseClasses} bg-pink-50 text-pink-800 border-pink-200`;
    case AppealRequestStatus.ASSIGNED_TO_PROSECUTOR:
      return `${baseClasses} bg-indigo-50 text-indigo-800 border-indigo-200`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_REGISTRAR:
      return `${baseClasses} bg-blue-100 text-blue-900 border-blue-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_CLERK:
      return `${baseClasses} bg-gray-100 text-gray-900 border-gray-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_COORDINATOR:
      return `${baseClasses} bg-purple-100 text-purple-900 border-purple-300`;
    case AppealRequestStatus.RESPONDED_BACK_TO_PROSECUTOR:
      return `${baseClasses} bg-orange-50 text-orange-800 border-orange-200`;
    case AppealRequestStatus.COMPLETED:
      return `${baseClasses} bg-green-100 text-green-900 border-green-300`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
  }
}

// Reusable Info Card Component
export const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
  subtitle?: string;
}> = ({ title, children, defaultOpen = false, badgeCount, subtitle }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
      <button
        className="group w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-gray-50/70 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`w-1.5 rounded-full bg-red-600 transition-all ${
              isOpen ? "h-8" : "h-6"
            }`}
          />
          <div className="min-w-0 text-left">
            <h3 className="text-base font-semibold text-gray-900 tracking-tight truncate">
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs text-gray-500 truncate">{subtitle}</p>
            )}
          </div>
          {badgeCount !== undefined && (
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 bg-red-50 text-red-700 text-xs font-semibold rounded-full border border-red-100">
              {badgeCount}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 shrink-0 text-gray-400 group-hover:text-gray-600 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-3 border-t border-gray-100">{children}</div>
      )}
    </div>
  );
};

// Reusable Info Row Component
export const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-start gap-4 py-2.5 border-b border-gray-100 last:border-b-0 ${className}`}
  >
    <dt className="text-sm text-gray-500 flex-shrink-0">{label}</dt>
    <dd className="text-sm text-gray-900 text-right font-medium break-words min-w-0">
      {value}
    </dd>
  </div>
);

// Reusable empty-state placeholder for sections with no data.
export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/60">
    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
      <svg
        className="w-5 h-5 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// File attachment component
export const FileAttachment: React.FC<{ name: string; url: string }> = ({
  name,
  url,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 shrink-0 bg-red-50 rounded-lg flex items-center justify-center">
          <svg
            className="w-5 h-5 text-red-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <span className="font-medium text-gray-900 truncate">{name}</span>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3 py-1 shrink-0 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
      >
        {t("view")}
      </a>
    </div>
  );
};

// Enhanced Action Button Component
export const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
  tooltip?: string;
}> = ({ onClick, children, variant = "outline", className = "", tooltip }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  const button = (
    <button
      type="button"
      title={tooltip}
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );

  if (!tooltip) return button;

  // Wrap with a professional hover tooltip (native title kept for a11y).
  return (
    <div className="relative inline-flex group/tip">
      {button}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/tip:opacity-100"
      >
        {tooltip}
        <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900" />
      </span>
    </div>
  );
};

// Action Dropdown Item Component
export const ActionDropdownItem: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
  >
    {children}
  </button>
);
